import { Pill } from "@/lib/bricks";
import { Section, List, Row, Empty, humanize } from "@/components/calm/Calm";
import { fmtDay } from "@/lib/dates";

// Read-only. Dispatch writes credentials, probation reviews and terminations;
// OS shows them so the owner can see where each vendor stands.

const CRED_STATUS = { expired: "Expired", expiring: "Expiring soon", valid: "Valid", no_expiry: "No expiry" };
const TRIGGER = { cancelled_after_accept: "Cancelled after accepting", no_show: "No check-in by start plus grace" };
const HOLD = { held: "Payout held", released: "Payout released", blocked: "Payout blocked" };

export function tradeLabel(v) {
  return v?.trade_label || humanize(v?.vendor_type || "photographer");
}

export function TradeBadge({ v }) {
  return <Pill tone="ink" data-testid="trade-badge">{tradeLabel(v)}</Pill>;
}

export function VettingChip({ v }) {
  const s = v?.vetting?.status;
  if (!s || !s.label) return null;
  return <Pill tone={s.attention ? "warn" : "neutral"} data-testid="vetting-chip">{s.label}</Pill>;
}

function credMeta(c) {
  const parts = [];
  if (c.expires_on) {
    const d = c.days_left;
    parts.push({ text: `Expires ${fmtDay(c.expires_on, { year: true })}${d != null && d >= 0 && d <= 30 ? `, in ${d} day${d === 1 ? "" : "s"}` : ""}`, attention: c.status !== "valid" });
  }
  if (c.province) parts.push({ text: c.province });
  if (c.limit_cents) parts.push({ text: `$${Math.round(c.limit_cents / 100).toLocaleString("en-CA")}`, figure: true });
  parts.push({ text: c.verified ? "Verified" : "Not verified", attention: !c.verified });
  return parts;
}

export default function VettingSection({ v }) {
  const vet = v?.vetting;
  if (!vet) return null;
  const pr = vet.probation;
  const creds = vet.credentials || [];
  const terms = vet.terminations || [];
  const probNote = !pr ? "No completed jobs yet. Probation covers the first jobs."
    : pr.state === "passed" ? `Passed. ${pr.approved} of ${pr.needed} jobs approved.`
    : pr.state === "failed" ? "Failed probation. Paused until you review."
    : `${pr.approved} of ${pr.needed} jobs approved.${pr.held ? ` ${pr.held} payout${pr.held === 1 ? "" : "s"} held${pr.next_release_after ? `, next release ${fmtDay(pr.next_release_after)}` : ""}.` : ""}`;
  return (
    <>
      {terms.length > 0 && (
        <Section testid="vendor-terminations" title="Terminations">
          <List>
            {terms.map((t, i) => (
              <Row key={`${t.created_at}-${i}`} title={TRIGGER[t.trigger] || humanize(t.trigger)}
                meta={[{ text: fmtDay(t.created_at, { year: true }) }, t.job_id ? { text: `Job ${t.job_id}`, figure: true } : null,
                  { text: `${(t.reassigned || []).length} job${(t.reassigned || []).length === 1 ? "" : "s"} reassigned` }].filter(Boolean)}
                status={t.undone_at ? `Undone ${fmtDay(t.undone_at)}` : "Active"} attention={!t.undone_at} />
            ))}
          </List>
        </Section>
      )}
      <Section testid="vendor-credentials" title="Credentials">
        {creds.length === 0 ? <Empty>No credentials on file.</Empty> : (
          <List>
            {creds.map((c, i) => (
              <Row key={`${c.kind}-${i}`} title={c.label} meta={credMeta(c)} status={CRED_STATUS[c.status]} attention={c.status === "expired" || c.status === "expiring"} />
            ))}
          </List>
        )}
      </Section>
      <Section testid="vendor-probation" title="Probation" note={probNote}>
        {(vet.probation_jobs || []).length > 0 && (
          <List>
            {vet.probation_jobs.map((r, i) => (
              <Row key={`${r.job_id}-${i}`} title={`Job ${r.job_id}`}
                meta={[r.delivered_at ? { text: `Delivered ${fmtDay(r.delivered_at)}` } : null,
                  r.client_rating ? { text: `${r.client_rating}/5 from client`, attention: r.client_rating <= 3 } : null,
                  r.payout_hold ? { text: HOLD[r.payout_hold] || humanize(r.payout_hold), attention: r.payout_hold === "blocked" } : null,
                  r.payout_hold === "held" && r.release_after ? { text: `Releases ${fmtDay(r.release_after)}` } : null,
                  r.hold_reason ? { text: r.hold_reason } : null].filter(Boolean)}
                status={humanize(r.status || "pending")} attention={r.status === "failed"} />
            ))}
          </List>
        )}
      </Section>
    </>
  );
}
